import { ContactsPanel, ContactForm } from 'pages/people/ContactsPanel';
import { DNTPanel, dntLoader, dntPanelAction } from 'pages/do-not-tow/DNTPanel';
import {
  permitTableAction,
  permitTableLoader,
} from 'pages/permits/PermitTable';
import {
  PermitTypesPanel,
  permitTypesAction,
  permitTypesLoader,
} from 'pages/permitTypes/PermitTypesPanel';
import {
  PropertyFiles,
  propertyFilesLoader,
} from 'pages/properties/PropertyFiles';
import {
  PropertyGroupLayout,
  propertyGroupLoader,
  propertyGroupsAction,
} from 'pages/property-groups/PropertyGroupLayout';
import {
  PropertyLayout,
  propertiesAction,
  propertyLoader,
} from 'pages/properties/PropertyLayout';
import PropertyPanel, {
  propertyPanelAction,
} from 'pages/properties/PropertyPanel';
import { QRCodesPanel, qrCodesAction } from 'pages/qrCodes/QRCodesPanel';
import {
  RuleForm,
  RulesPanel,
  rulesAction,
  rulesLoader,
} from 'pages/rules/RulesPanel';
import {
  RulesetForm,
  RulesetsPanel,
  rulesetsAction,
  rulesetsLoader,
} from 'pages/rules/RulesetsPanel';
import { UserForm, userFormLoader, UsersPanel } from 'pages/people/UsersPanel';
import { tenantFormLoader } from 'pages/command-center/TenantForm';
import ChangePassword from 'pages/change-password/ChangePassword';
import EmailVerification from '../pages/login/emailVerification';
import ErrorPage from 'pages/error';
import JobBuilderContextProvider from 'context/jobBuilderContext';
import JobsCreate from 'pages/jobs/create/JobsCreate';
import JobsDetails from 'pages/jobs/details/JobDetails';
import { JobsPanel } from 'pages/jobs/JobsPanel';
import Login, { loginAction } from 'pages/login';
import SSO, { ssoLoader } from 'pages/sso';
import Permissions from 'pages/permissions';
import PermitFormLayout, {
  permitFormLoader,
  permitFormAction,
} from 'pages/permits/PermitFormLayout';
import { PermitTypesForm } from 'pages/permitTypes/PermitTypesForm';
import PrivateRoute from 'router/PrivateRoute';
import { PropertyBranding } from 'pages/properties/PropertyBranding';
import PropertyCreate from 'pages/properties/PropertyCreate';
import PropertyDetails from 'pages/properties/PropertyDetails';
import { PropertyGroupDetails } from 'pages/property-groups/PropertyGroupDetails';
import { QRCodesForm } from 'pages/qrCodes/QRCodesForm';
import { ROUTES } from './routes';
import RegisterUser from 'pages/registration';
import CompanyDetails, {
  companyDetailsAction,
  companyDetailsLoader,
} from 'pages/tenants/CompanySetup';
import Violations from 'pages/rules/Violations';
import {
  createBrowserRouter,
  LoaderFunctionArgs,
  redirect,
  ShouldRevalidateFunctionArgs,
} from 'react-router';
import { jobDetailsLoader } from 'pages/jobs/details/JobDetails';
import jobsAction from 'pages/jobs/jobsAction';
import {
  jobPropertyDataLoader,
  jobsCreatePropertyLoader,
} from 'pages/jobs/create/jobPropertyDataLoader';
import { cancelJobLoader, jobsLoader } from 'pages/jobs/JobsPanel';
import permissionsAction from 'pages/permissions/permissionsAction';
import permissionsLoader from 'pages/permissions/permissionsLoader';
import { qrCodesLoader } from 'pages/qrCodes/QRCodesPanel';
import { setCookieValue, getCookieValue } from 'utils';
import violationsAction from 'pages/rules/violationsAction';
import violationsLoader from 'pages/rules/violationsLoader';
import {
  permitWizardAction,
  permitImportWizardLoader,
} from 'pages/permits/migrationWizard/PermitImportWizard';
import PermitDetails from 'pages/permits/PermitDetails';
import PermitRefunds from 'pages/permits/PermitRefunds';
import PermitHistory, {
  permitHistoryLoader,
} from 'pages/permits/PermitHistory';
import PermitTable from 'pages/permits/PermitTable';
import * as Sentry from '@sentry/react';
import PublicLayout from 'components/layout/PublicLayout';
import { routeWithParams } from 'router';
import AuthService from 'utils/AuthService';
import ActivityFeed, { activityFeedAction } from 'pages/dashboard/ActivityFeed';
import ReportsPanel from 'pages/reports/ReportsPanel';
import Dashboard, {
  loader as dashboardLoader,
} from 'pages/dashboard/Dashboard';
import PropertyGroupPanel, {
  propertyGroupAction,
} from 'pages/property-groups/PropertyGroupPanel';
import PropertyGroupCreate from 'pages/property-groups/PropertyGroupCreate';
import RulesFormLayout from 'pages/rules/RulesFormLayout';
import {
  InstructionsPanel,
  instructionsAction,
  instructionsLoader,
} from 'pages/rules/InstructionsPanel';
import TenantSelect from 'pages/tenants/TenantSelect';
import PropertyReport, {
  propertyReportLoader,
} from 'pages/reports/PropertyReport';
import PeopleTabLayout, {
  action as peopleTabAction,
  loader as peopleTabLoader,
} from 'pages/people/PeopleTabLayout';
import UserProfile, {
  userProfileLoader,
  userProfileAction,
} from 'pages/user-profile/UserProfile';
import { PropertyDetailsLayout } from 'pages/properties/PropertyDetailsLayout';
import PropertyGroupDetailsLayout from 'pages/property-groups/PropertyGroupDetailsLayout';
import ProtectedRoute from './ProtectedRoute';
import { activityFeedLoader } from 'pages/dashboard/ActivityFeed';
import PermitImportWizard from 'pages/permits/migrationWizard/PermitImportWizard';
import PropertyImportWizard, {
  propertyImportWizardAction,
} from 'pages/properties/importWizard/PropertyImportWizard';
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const rootLoader = ({ request, params }: LoaderFunctionArgs) => {
  const companyPath = params.companyPath;
  if (companyPath) {
    setCookieValue('companyPath', companyPath);
  }
  const url = new URL(request.url);
  const pathname = url.pathname;

  if (pathname === '/' || pathname === `/${companyPath}`) {
    return redirect(routeWithParams(ROUTES.LOGIN, [companyPath || '']));
  }
};

const authLoader = () => {
  if (!AuthService.IsLoggedIn()) {
    const companyPath = getCookieValue('companyPath');
    return redirect(routeWithParams(ROUTES.LOGIN, [companyPath || '']));
  }
};

const router = sentryCreateBrowserRouter([
  {
    path: ROUTES.INDEX,
    errorElement: <ErrorPage />, // error boundary
    loader: rootLoader,
    children: [
      {
        id: 'public-routes',
        element: <PublicLayout />,
        children: [
          {
            path: '*',
            element: <Login />,
            action: loginAction,
          },
          {
            path: ROUTES.LOGIN,
            element: <Login />,
            action: loginAction,
          },
          {
            path: ROUTES.SSO,
            element: <SSO />,
            loader: ssoLoader,
          },
          {
            path: ROUTES.CHANGE_PASSWORD,
            element: <ChangePassword />,
          },
          {
            path: ROUTES.CHANGE_PASSWORD_TOKEN,
            element: <ChangePassword />,
          },
          {
            path: ROUTES.REGISTRATION,
            element: <RegisterUser />,
          },
          {
            path: ROUTES.EMAIL_VERIFICATION,
            element: <EmailVerification />,
          },
          {
            path: ROUTES.REDIRECT_ERROR,
            element: <ErrorPage />,
          },
        ],
      },
      {
        id: 'authenticated-routes',
        loader: authLoader,
        errorElement: <ErrorPage />, // error boundary
        element: <ProtectedRoute />,
        children: [
          {
            id: ROUTES.DASHBOARD,
            path: ROUTES.DASHBOARD,
            element: <Dashboard />,
            loader: dashboardLoader,
            shouldRevalidate: ({ currentUrl, nextUrl, currentParams, nextParams, defaultShouldRevalidate } : ShouldRevalidateFunctionArgs) => {
              const isNavigation = currentUrl.pathname !== nextUrl.pathname;
              if (!isNavigation) {
                return false;
              }
              const isNewTenant = currentParams['tenantID'] !== nextParams['tenantID'];
              if (isNewTenant) {
                return true;
              }
              const isPropertyOrPropertyGroupTab = nextUrl.pathname.includes('properties') || nextUrl.pathname.includes('property-groups');
              if (isPropertyOrPropertyGroupTab) {
                return true;
              }
              return defaultShouldRevalidate;
            },
            errorElement: <ErrorPage />,
            children: [
              {
                path: ROUTES.JOBS,
                action: jobsAction,
                element: <JobsPanel />,
                loader: jobsLoader,
              },
              {
                path: ROUTES.JOBS_DETAILS,
                loader: jobDetailsLoader,
                element: (
                  <PrivateRoute
                    path={ROUTES.JOBS_DETAILS}
                    element={<JobsDetails />}
                  />
                ),
              },
              {
                path: ROUTES.JOBS_CREATE,
                action: jobsAction,
                element: (
                  <PrivateRoute
                    path={ROUTES.JOBS_CREATE}
                    element={
                      <JobBuilderContextProvider>
                        <JobsCreate />
                      </JobBuilderContextProvider>
                    }
                  />
                ),
              },
              {
                path: ROUTES.ACTIVITY_FEED,
                loader: activityFeedLoader,
                action: activityFeedAction,
                element: (
                  <PrivateRoute
                    path={ROUTES.ACTIVITY_FEED}
                    element={<ActivityFeed />}
                  />
                ),
              },
              {
                path: ROUTES.PROPERTIES,
                action: propertyPanelAction,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTIES}
                    element={<PropertyPanel />}
                  />
                ),
                children: [
                  {
                    path: ROUTES.PROPERTY_CREATE,
                    element: <PropertyCreate />,
                    viewTransition: true,
                  },
                ],
              },
              {
                path: ROUTES.PROPERTY_GROUPS,
                action: propertyGroupAction,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_GROUP}
                    element={<PropertyGroupPanel />}
                  />
                ),
              },
              {
                path: ROUTES.PROPERTY_GROUP_CREATE,
                action: propertyGroupAction,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_GROUP_CREATE}
                    element={<PropertyGroupCreate />}
                  />
                ),
              },
              {
                path: ROUTES.PERMISSIONS,
                element: (
                  <PrivateRoute
                    path={ROUTES.PERMISSIONS}
                    element={<Permissions />}
                  />
                ),
                action: permissionsAction,
                loader: permissionsLoader,
              },
              {
                path: ROUTES.PEOPLE,
                id: ROUTES.PEOPLE,
                element: <PeopleTabLayout />,
                action: peopleTabAction,
                loader: peopleTabLoader,
                children: [
                  {
                    path: ROUTES.USERS,
                    id: ROUTES.USERS,
                    element: (
                      <PrivateRoute
                        path={ROUTES.USERS}
                        element={<UsersPanel />}
                      />
                    ),
                  },
                  {
                    path: ROUTES.USERS_FORM,
                    loader: userFormLoader,
                    element: (
                      <PrivateRoute
                        path={ROUTES.USERS}
                        element={<UserForm />}
                      />
                    ),
                  },
                  {
                    path: ROUTES.CONTACTS,
                    element: (
                      <PrivateRoute
                        path={ROUTES.CONTACTS}
                        element={<ContactsPanel />}
                      />
                    ),
                    children: [
                      {
                        path: ROUTES.CONTACTS_FORM,
                        element: <ContactForm />,
                      },
                    ],
                  },
                ],
              },
              {
                path: ROUTES.TENANT_SELECT,
                element: <TenantSelect />,
              },
              {
                path: ROUTES.RULES_CONFIG,
                element: (
                  <PrivateRoute
                    path={ROUTES.RULES_CONFIG}
                    element={<RulesFormLayout />}
                  />
                ),
                children: [
                  {
                    path: ROUTES.RULES,
                    element: (
                      <PrivateRoute
                        path={ROUTES.RULES}
                        element={<RulesPanel />}
                      />
                    ),
                    loader: rulesLoader,
                    action: rulesAction,
                    children: [
                      {
                        path: ROUTES.RULES_FORM,
                        action: rulesAction,
                        element: (
                          <PrivateRoute
                            path={ROUTES.RULES_FORM}
                            element={<RuleForm />}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    path: ROUTES.RULESETS,
                    loader: rulesetsLoader,
                    action: rulesetsAction,
                    element: (
                      <PrivateRoute
                        path={ROUTES.RULESETS}
                        element={<RulesetsPanel />}
                      />
                    ),
                    children: [
                      {
                        path: ROUTES.RULESETS_FORM,
                        action: rulesetsAction,
                        element: (
                          <PrivateRoute
                            path={ROUTES.RULESETS_FORM}
                            element={<RulesetForm />}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    path: ROUTES.VIOLATIONS,
                    element: (
                      <PrivateRoute
                        path={ROUTES.VIOLATIONS}
                        element={<Violations />}
                      />
                    ),
                    loader: violationsLoader,
                    action: violationsAction,
                  },
                ],
              },
              {
                path: ROUTES.COMPANY_SETUP,
                action: companyDetailsAction,
                loader: companyDetailsLoader,
                element: (
                  <PrivateRoute
                    path={ROUTES.COMPANY_SETUP}
                    element={<CompanyDetails />}
                  />
                ),
              },
              {
                path: ROUTES.COMMAND_CENTER,
                lazy: async () =>
                  await import('pages/command-center/CommandLayout'),
                children: [
                  {
                    path: ROUTES.HEALTH,
                    lazy: async () =>
                      await import('pages/command-center/HealthMonitor'),
                  },
                  {
                    path: ROUTES.SYSTEMS,
                    lazy: async () =>
                      await import('pages/command-center/SystemPanel'),
                    children: [
                      {
                        path: ROUTES.SYSTEMS_FORM,
                        lazy: async () =>
                          await import('pages/command-center/SystemForm'),
                      },
                    ],
                  },
                  {
                    path: ROUTES.TENANT_GROUPS,
                    lazy: async () =>
                      await import('pages/command-center/TenantGroupPanel'),
                    children: [
                      {
                        path: ROUTES.TENANT_GROUPS_FORM,
                        lazy: async () =>
                          await import('pages/command-center/TenantGroupForm'),
                      },
                    ],
                  },
                  {
                    path: ROUTES.TENANTS,
                    lazy: async () =>
                      await import('pages/command-center/TenantPanel'),
                    children: [
                      {
                        path: ROUTES.TENANTS_FORM,
                        lazy: async () =>
                          await import('pages/command-center/TenantForm'),
                        loader: tenantFormLoader,
                      },
                    ],
                  },
                  {
                    path: ROUTES.PROPERTY_IMPORT,
                    element: <PropertyImportWizard />,
                    action: propertyImportWizardAction,
                  },
                ],
              },
              {
                path: ROUTES.REPORTS,
                element: <ReportsPanel />,
                children: [
                  {
                    path: ROUTES.REPORTS_JOBS_BY_PROPERTY,
                    element: <PropertyReport />,
                    loader: propertyReportLoader,
                  },
                ],
              },
              {
                id: ROUTES.USER_PROFILE,
                path: ROUTES.USER_PROFILE,
                element: <UserProfile />,
                errorElement: <ErrorPage />,
                loader: userProfileLoader,
                action: userProfileAction,
              },
            ],
          },
          {
            path: ROUTES.PROPERTY,
            id: ROUTES.PROPERTY,
            loader: propertyLoader,
            action: propertiesAction,
            errorElement: <ErrorPage />,
            element: (
              <PrivateRoute
                path={ROUTES.PROPERTY}
                element={<PropertyLayout />}
              />
            ),
            children: [
              {
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY}
                    element={<PropertyDetailsLayout />}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <PropertyDetails />,
                  },
                  {
                    path: ROUTES.PROPERTY_QR_CODES,
                    loader: qrCodesLoader,
                    action: qrCodesAction,
                    element: <QRCodesPanel />,
                    children: [
                      {
                        path: ROUTES.PROPERTY_QR_CODES_FORM,
                        element: <QRCodesForm />,
                      },
                    ],
                  },
                  {
                    path: ROUTES.PROPERTY_FILES,
                    loader: propertyFilesLoader,
                    element: <PropertyFiles />,
                  },
                  {
                    path: ROUTES.PROPERTY_BRANDING,
                    element: <PropertyBranding />,
                  },
                  {
                    path: ROUTES.PROPERTY_DO_NOT_TOW,
                    id: ROUTES.PROPERTY_DO_NOT_TOW,
                    action: dntPanelAction,
                    loader: dntLoader,
                    element: <DNTPanel />,
                  },
                ],
              },
              {
                path: ROUTES.PROPERTY_PEOPLE,
                id: ROUTES.PROPERTY_PEOPLE,
                element: <PeopleTabLayout />,
                action: peopleTabAction,
                loader: peopleTabLoader,
                errorElement: <ErrorPage />,
                children: [
                  {
                    path: ROUTES.PROPERTY_CONTACTS,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_CONTACTS}
                        element={<ContactsPanel />}
                      />
                    ),
                    children: [
                      {
                        path: ROUTES.PROPERTY_CONTACTS_FORM,
                        element: <ContactForm />,
                      },
                    ],
                  },
                  {
                    path: ROUTES.PROPERTY_USERS,
                    id: ROUTES.PROPERTY_USERS,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_USERS}
                        element={<UsersPanel />}
                      />
                    ),
                  },
                  {
                    path: ROUTES.PROPERTY_USERS_FORM,
                    loader: userFormLoader,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_USERS_FORM}
                        element={<UserForm />}
                      />
                    ),
                  },
                ],
              },
              {
                path: ROUTES.PROPERTY_RULES_CONFIG,
                errorElement: <ErrorPage />,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_RULES_CONFIG}
                    element={<RulesFormLayout />}
                  />
                ),
                children: [
                  {
                    path: ROUTES.PROPERTY_RULES,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_RULES}
                        element={<RulesPanel />}
                      />
                    ),
                    loader: rulesLoader,
                    action: rulesAction,
                    children: [
                      {
                        path: ROUTES.PROPERTY_RULES_FORM,
                        action: rulesAction,
                        element: (
                          <PrivateRoute
                            path={ROUTES.PROPERTY_RULES_FORM}
                            element={<RuleForm />}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    path: ROUTES.PROPERTY_RULESETS,
                    loader: rulesetsLoader,
                    action: rulesetsAction,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_RULESETS}
                        element={<RulesetsPanel />}
                      />
                    ),
                    children: [
                      {
                        path: ROUTES.PROPERTY_RULESETS_FORM,
                        action: rulesetsAction,
                        element: (
                          <PrivateRoute
                            path={ROUTES.PROPERTY_RULESETS_FORM}
                            element={<RulesetForm />}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    path: ROUTES.PROPERTY_INSTRUCTIONS,
                    loader: instructionsLoader,
                    action: instructionsAction,
                    element: <InstructionsPanel />,
                  },
                ],
              },
              {
                path: ROUTES.PROPERTY_PERMIT_TYPES,
                loader: permitTypesLoader,
                action: permitTypesAction,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_PERMIT_TYPES}
                    element={<PermitTypesPanel />}
                  />
                ),
                children: [
                  {
                    path: ROUTES.PROPERTY_PERMIT_TYPES_FORM,
                    action: permitTypesAction,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_PERMIT_TYPES_FORM}
                        element={<PermitTypesForm />}
                      />
                    ),
                  },
                ],
              },
              {
                id: ROUTES.PROPERTY_PERMITS,
                path: ROUTES.PROPERTY_PERMITS,
                loader: permitTableLoader,
                action: permitTableAction,
                errorElement: <ErrorPage />,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_PERMITS}
                    element={<PermitTable />}
                  />
                ),
              },
              {
                id: ROUTES.PROPERTY_PERMIT_DETAILS,
                path: ROUTES.PROPERTY_PERMIT_DETAILS,
                loader: permitFormLoader,
                action: permitFormAction,
                errorElement: <ErrorPage />,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_PERMIT_DETAILS}
                    element={<PermitFormLayout />}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <PermitDetails />,
                  },
                  {
                    path: ROUTES.PROPERTY_PERMIT_REFUNDS,
                    element: <PermitRefunds />,
                  },
                  {
                    path: ROUTES.PROPERTY_PERMIT_HISTORY,
                    element: <PermitHistory />,
                    loader: permitHistoryLoader,
                  },
                ],
              },
              {
                id: ROUTES.PROPERTY_PERMITS_IMPORT,
                path: ROUTES.PROPERTY_PERMITS_IMPORT,
                action: permitWizardAction,
                loader: permitImportWizardLoader,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_PERMITS_IMPORT}
                    element={<PermitImportWizard />}
                  />
                ),
                errorElement: <ErrorPage />,
              },
              {
                id: ROUTES.PROPERTY_JOBS,
                path: ROUTES.PROPERTY_JOBS,
                loader: jobsLoader,
                action: jobsAction,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_JOBS}
                    element={<JobsPanel />}
                  />
                ),
              },
              {
                path: ROUTES.PROPERTY_JOBS_DETAILS,
                loader: jobDetailsLoader,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_JOBS_DETAILS}
                    element={<JobsDetails />}
                  />
                ),
              },
              {
                path: ROUTES.PROPERTY_JOBS_CREATE,
                action: jobsAction,
                loader: jobsCreatePropertyLoader,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_JOBS_CREATE}
                    element={
                      <JobBuilderContextProvider>
                        <JobsCreate />
                      </JobBuilderContextProvider>
                    }
                  />
                ),
              },
            ],
          },
          {
            path: ROUTES.JOB_PROPERTY_DATA,
            loader: jobPropertyDataLoader,
          },
          {
            path: ROUTES.CANCEL_JOB_REASONS,
            loader: cancelJobLoader,
          },
          {
            path: ROUTES.PROPERTY_GROUP,
            id: ROUTES.PROPERTY_GROUP,
            loader: propertyGroupLoader,
            action: propertyGroupsAction,
            element: (
              <PrivateRoute
                path={ROUTES.PROPERTY_GROUP}
                element={<PropertyGroupLayout />}
              />
            ),
            children: [
              {
                path: ROUTES.PROPERTY_GROUP,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_GROUP}
                    element={<PropertyGroupDetailsLayout />}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <PropertyGroupDetails />,
                    action: propertyGroupsAction,
                  },
                  {
                    path: ROUTES.PROPERTY_GROUP_DO_NOT_TOW,
                    id: ROUTES.PROPERTY_GROUP_DO_NOT_TOW,
                    action: dntPanelAction,
                    loader: dntLoader,
                    element: <DNTPanel />,
                  },
                  {
                    path: ROUTES.PROPERTY_GROUP_QR_CODES,
                    loader: qrCodesLoader,
                    action: qrCodesAction,
                    element: <QRCodesPanel />,
                    children: [
                      {
                        path: ROUTES.PROPERTY_GROUP_QR_CODES_FORM,
                        element: <QRCodesForm />,
                      },
                    ],
                  },
                  {
                    path: ROUTES.PROPERTY_GROUP_FILES,
                    element: <PropertyFiles />,
                    loader: propertyFilesLoader,
                  },
                  {
                    path: ROUTES.PROPERTY_GROUP_BRANDING,
                    action: propertyGroupsAction,
                    element: <PropertyBranding />,
                  },
                ],
              },
              {
                path: ROUTES.PROPERTY_GROUP_PROPERTIES,
                action: propertyPanelAction,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_GROUP_PROPERTIES}
                    element={<PropertyPanel />}
                  />
                ),
                children: [
                  {
                    path: ROUTES.PROPERTY_GROUP_PROPERTIES_CREATE,
                    action: propertyPanelAction,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_GROUP_PROPERTIES_CREATE}
                        element={<PropertyCreate />}
                      />
                    ),
                  },
                ],
              },
              {
                path: ROUTES.PROPERTY_GROUP_PEOPLE,
                id: ROUTES.PROPERTY_GROUP_PEOPLE,
                element: <PeopleTabLayout />,
                action: peopleTabAction,
                loader: peopleTabLoader,
                children: [
                  {
                    path: ROUTES.PROPERTY_GROUP_CONTACTS,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_GROUP_CONTACTS}
                        element={<ContactsPanel />}
                      />
                    ),
                    children: [
                      {
                        path: ROUTES.PROPERTY_GROUP_CONTACTS_FORM,
                        element: <ContactForm />,
                      },
                    ],
                  },
                  {
                    path: ROUTES.PROPERTY_GROUP_USERS,
                    id: ROUTES.PROPERTY_GROUP_USERS,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_GROUP_USERS}
                        element={<UsersPanel />}
                      />
                    ),
                  },
                  {
                    id: ROUTES.PROPERTY_GROUP_USERS_FORM,
                    path: ROUTES.PROPERTY_GROUP_USERS_FORM,
                    loader: userFormLoader,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_GROUP_USERS_FORM}
                        element={<UserForm />}
                      />
                    ),
                  },
                ],
              },
              {
                path: ROUTES.PROPERTY_GROUP_RULES_CONFIG,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_GROUP_RULES_CONFIG}
                    element={<RulesFormLayout />}
                  />
                ),
                children: [
                  {
                    path: ROUTES.PROPERTY_GROUP_RULES,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_GROUP_RULES}
                        element={<RulesPanel />}
                      />
                    ),
                    loader: rulesLoader,
                    action: rulesAction,
                    children: [
                      {
                        path: ROUTES.PROPERTY_GROUP_RULES_FORM,
                        action: rulesAction,
                        element: (
                          <PrivateRoute
                            path={ROUTES.PROPERTY_GROUP_RULES_FORM}
                            element={<RuleForm />}
                          />
                        ),
                      },
                    ],
                  },
                  {
                    path: ROUTES.PROPERTY_GROUP_RULESETS,
                    loader: rulesetsLoader,
                    action: rulesetsAction,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_GROUP_RULESETS}
                        element={<RulesetsPanel />}
                      />
                    ),
                    children: [
                      {
                        path: ROUTES.PROPERTY_GROUP_RULESETS_FORM,
                        action: rulesetsAction,
                        element: (
                          <PrivateRoute
                            path={ROUTES.PROPERTY_GROUP_RULESETS_FORM}
                            element={<RulesetForm />}
                          />
                        ),
                      },
                    ],
                  },
                ],
              },
              {
                path: ROUTES.PROPERTY_GROUP_PERMIT_TYPES,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_GROUP_PERMIT_TYPES}
                    element={<PermitTypesPanel />}
                  />
                ),
                loader: permitTypesLoader,
                action: permitTypesAction,
                children: [
                  {
                    path: ROUTES.PROPERTY_GROUP_PERMIT_TYPES_FORM,
                    action: permitTypesAction,
                    element: (
                      <PrivateRoute
                        path={ROUTES.PROPERTY_GROUP_PERMIT_TYPES_FORM}
                        element={<PermitTypesForm />}
                      />
                    ),
                  },
                ],
              },
              {
                id: ROUTES.PROPERTY_GROUP_JOBS,
                path: ROUTES.PROPERTY_GROUP_JOBS,
                loader: jobsLoader,
                action: jobsAction,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_GROUP_JOBS}
                    element={<JobsPanel />}
                  />
                ),
              },
              {
                path: ROUTES.PROPERTY_GROUP_JOBS_DETAILS,
                loader: jobDetailsLoader,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_GROUP_JOBS_DETAILS}
                    element={<JobsDetails />}
                  />
                ),
              },
              {
                path: ROUTES.PROPERTY_GROUP_JOBS_CREATE,
                action: jobsAction,
                element: (
                  <PrivateRoute
                    path={ROUTES.PROPERTY_GROUP_JOBS_CREATE}
                    element={
                      <JobBuilderContextProvider>
                        <JobsCreate />
                      </JobBuilderContextProvider>
                    }
                  />
                ),
              },
            ],
          },
          {
            path: ROUTES.REVISIONS,
            lazy: () => import('pages/revisions'),
          },
        ],
      },
    ],
  },
]);

export * from './routes';
export default router;
